import { cn } from '@/util/cn'
import { IMAGE_PATH } from '@/app/(site)/(static)/about/config/constants'
import Typography from '@/app/components/typography'
import { Slide as SlideType } from '../../../../slides'
import { ReactNode } from 'react'
import { AnimatePresence, motion, Transition, useReducedMotion, Variants } from 'framer-motion'
import useCarouselState from '../../../../hooks/use-carousel-state'

type Props = {
    classNames?: Partial<Record<'base' | 'content', string>>
    endContent?: ReactNode
    renderName?: (name: SlideType['name']) => ReactNode
} & SlideType & Pick<ReturnType<typeof useCarouselState>, 'animationHandlers'>

const imgVariants: Variants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit:    { opacity: 0 },
}

const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: 20 },
}

const Slide = ({
   name, text, link, linkText, image, gradientClassName, classNames, endContent, animationHandlers, renderName,
}: Props) => {
    const href = link || `search/${name}`
    const prefersReducedMotion = useReducedMotion()

    const transition: Transition = prefersReducedMotion
        ? { duration: 0 }
        : { duration: 0.15, ease: 'easeInOut' }

    return (
        <div className={cn('flex flex-col rounded-lg overflow-hidden relative', gradientClassName, classNames?.base)}>
            <div className="relative aspect-[16/9] overflow-hidden">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                        key={image}
                        alt={name}
                        src={`${IMAGE_PATH}/values/${image}`}
                        variants={imgVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        transition={transition}
                        className="size-full object-cover rounded-md"
                        {...animationHandlers}
                    />
                </AnimatePresence>
            </div>

            <div className="py-10 px-5 lg:p-10 drop-shadow relative">
                <div className="min-h-[8rem]">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                            key={name}
                            variants={contentVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={transition}
                            className={cn('grid gap-4.5', classNames?.content)}
                            {...animationHandlers}
                        >
                            {typeof renderName === 'function' ? renderName(name) : (
                                <Typography variant="h2" fontFamily="nexaBlack">{name}</Typography>
                            )}

                            <Typography variant="text-lg">{text}</Typography>

                            <a href={href} className="inline-flex items-center gap-2">
                                <Typography variant="text-lg" fontWeight="bold" className="opacity-75">
                                    See all the {linkText || name} GIFs
                                </Typography>
                                <i className="ss-icon ss-navigateright text-[0.75rem] leading-none" />
                            </a>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {endContent}
        </div>
    )
}

export default Slide
